import React, { useState, useEffect } from 'react';
import { useDocumentTemplate, useUpdateDocumentTemplate } from '@/hooks/useDocumentTemplates';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Save, Loader2, CheckCircle2, Edit, Eye } from 'lucide-react';

export const NDATemplateEditor: React.FC = () => {
  const { data: template, isLoading } = useDocumentTemplate('nda');
  const updateMutation = useUpdateDocumentTemplate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    preambule: '',
    articles: [] as Array<{ number: number; title: string; content: string }>,
    footer: '',
  });

  useEffect(() => {
    if (template?.content) {
      setFormData({
        title: template.content.title || '',
        subtitle: template.content.subtitle || '',
        preambule: template.content.preambule || '',
        articles: template.content.articles || [],
        footer: template.content.footer || '',
      });
    }
  }, [template]);

  const handleSave = async () => {
    if (!template) return;

    try {
      await updateMutation.mutateAsync({
        id: template.id,
        updates: {
          content: formData as any,
        },
      });
      
      setIsSaved(true);
      setIsEditing(false);
      setTimeout(() => setIsSaved(false), 3000);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde du template');
    }
  };

  const updateArticle = (index: number, field: 'title' | 'content', value: string) => {
    const newArticles = [...formData.articles];
    newArticles[index] = { ...newArticles[index], [field]: value };
    setFormData({ ...formData, articles: newArticles });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-stratos-gold animate-spin" />
      </div>
    );
  }

  if (!template) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-600 mb-4">Template NDA non trouvé</p>
          <p className="text-sm text-gray-500">
            Exécutez la migration 005_create_document_templates.sql
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Éditeur de Template NDA</CardTitle>
              <CardDescription>
                Modifiez le contenu par défaut du NDA pour tous les clients
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {isSaved && (
                <Badge variant="success" className="gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Sauvegardé
                </Badge>
              )}
              {!isEditing ? (
                <Button
                  onClick={() => setIsEditing(true)}
                  className="bg-stratos-gold hover:bg-stratos-gold-dark text-gray-900"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      // Reset to original
                      if (template?.content) {
                        setFormData({
                          title: template.content.title || '',
                          subtitle: template.content.subtitle || '',
                          preambule: template.content.preambule || '',
                          articles: template.content.articles || [],
                          footer: template.content.footer || '',
                        });
                      }
                    }}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {updateMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Sauvegarder
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Form */}
      <Card className="p-6">
        <div className="space-y-6">
          {/* Title & Subtitle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Titre du document</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                disabled={!isEditing}
                className={!isEditing ? 'bg-gray-50' : ''}
              />
            </div>
            <div>
              <Label htmlFor="subtitle">Sous-titre</Label>
              <Input
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                disabled={!isEditing}
                className={!isEditing ? 'bg-gray-50' : ''}
              />
            </div>
          </div>

          {/* Préambule */}
          <div>
            <Label htmlFor="preambule">Préambule</Label>
            <textarea
              id="preambule"
              value={formData.preambule}
              onChange={(e) => setFormData({ ...formData, preambule: e.target.value })}
              disabled={!isEditing}
              className={`w-full border border-gray-300 rounded-md p-3 text-sm min-h-[100px] ${
                !isEditing ? 'bg-gray-50' : ''
              }`}
            />
          </div>

          {/* Articles */}
          <div className="space-y-4">
            <Label>Articles ({formData.articles.length})</Label>
            {formData.articles.map((article, index) => (
              <Card key={article.number} className="p-4 bg-gray-50">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">Article {article.number}</Badge>
                    <Input
                      value={article.title}
                      onChange={(e) => updateArticle(index, 'title', e.target.value)}
                      disabled={!isEditing}
                      className={`flex-1 font-medium ${!isEditing ? 'bg-white' : ''}`}
                      placeholder="Titre de l'article"
                    />
                  </div>
                  <textarea
                    value={article.content}
                    onChange={(e) => updateArticle(index, 'content', e.target.value)}
                    disabled={!isEditing}
                    className={`w-full border border-gray-300 rounded-md p-3 text-sm min-h-[150px] ${
                      !isEditing ? 'bg-white' : ''
                    }`}
                    placeholder="Contenu de l'article"
                  />
                </div>
              </Card>
            ))}
          </div>

          {/* Footer */}
          <div>
            <Label htmlFor="footer">Footer</Label>
            <Input
              id="footer"
              value={formData.footer}
              onChange={(e) => setFormData({ ...formData, footer: e.target.value })}
              disabled={!isEditing}
              className={!isEditing ? 'bg-gray-50' : ''}
            />
          </div>
        </div>
      </Card>

      {/* Preview Info */}
      {!isEditing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Eye className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-medium">
                Mode Lecture
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Cliquez sur "Modifier" pour éditer le contenu. Les modifications seront appliquées à tous les nouveaux NDA générés.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

